import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Sparkles, Check } from 'lucide-react';

interface UpgradePromptCardProps {
  currentPlan: string;
  userRole?: string;
  onUpgradeClick: () => void;
}

const getUpgradeBenefits = (currentPlan: string, userRole?: string): string[] => {
  // SPONSORED - benefícios focados em visibilidade, credibilidade e destaque
  // Sponsored já tem ações ilimitadas, então foco é em se destacar
  if (userRole === 'sponsored' || currentPlan.includes('sponsored')) {
    return [
      'Destaque permanente no feed - apareça primeiro',
      'Filtros avançados de busca (renda, estilo de vida)',
      'Selo Premium exclusivo no seu perfil',
      'Prioridade para novos sponsors que entram',
      '3x mais chances de match comprovado'
    ];
  }
  
  // SPONSOR FREE → INSPIRE
  if (currentPlan.includes('free')) {
    return [
      'Visualizações ilimitadas de perfis',
      '30 curtidas por dia',
      'Conversas ilimitadas',
      'Filtros avançados de busca',
      '1 boost por mês (3 por trimestre)'
    ];
  }
  
  // SPONSOR STANDARD → INFINITY
  if (currentPlan.includes('standard')) {
    return [
      'Tudo ilimitado sem restrições',
      'Acesso imediato a perfis novos',
      'Filtro exclusivo de verificados',
      'Destaque permanente no feed',
      '4 boosts por mês (12 por trimestre)',
      'Suporte prioritário 24/7'
    ];
  }
  
  return [];
};

export const UpgradePromptCard: React.FC<UpgradePromptCardProps> = ({
  currentPlan,
  userRole,
  onUpgradeClick
}) => {
  const benefits = getUpgradeBenefits(currentPlan, userRole);
  const isSponsored = userRole === 'sponsored' || currentPlan.includes('sponsored');
  // Sponsored só tem um plano pago (Infinity), Sponsor free → Inspire, standard → Infinity
  const nextPlanName = isSponsored ? 'Infinity' : (currentPlan.includes('free') ? 'Inspire' : 'Infinity');
  const isInfinity = currentPlan.includes('premium');

  // Don't show for Infinity plan
  if (isInfinity || benefits.length === 0) {
    return null;
  }

  return (
    <Card className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm overflow-hidden">
      <CardHeader>
        <CardTitle className="text-white text-xl font-semibold flex items-center gap-2">
          <div className="w-10 h-10 bg-luxury-gold/20 rounded-full flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-luxury-pinkBright" />
          </div>
          <span>Desbloqueie mais recursos</span>
        </CardTitle>
        <p className="text-sm text-white/60 mt-2">
          Upgrade para <span className="text-luxury-pinkBright font-semibold">{nextPlanName}</span> e aproveite:
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <ul className="space-y-3">
          {benefits.map((benefit, index) => (
            <li key={index} className="flex items-center gap-3">
              <div className="w-8 h-8 bg-luxury-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="h-4 w-4 text-luxury-pinkBright" />
              </div>
              <span className="text-white/80 text-sm">{benefit}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={onUpgradeClick}
          className="w-full bg-gradient-to-b from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white font-semibold shadow-lg"
        >
          <Crown className="h-4 w-4 mr-2" />
          Fazer Upgrade para {nextPlanName}
        </Button>
      </CardContent>
    </Card>
  );
};
